import { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';

import { Modal, TokenLogo } from 'components';
import { TOKEN_BY_ADDRESS } from 'consts';
import { store } from 'store';
import { WowmaxQuoteResponse } from 'types/aggregation';
import { bn } from 'utils/BigNumber';
import { cutAddress, cutTokenSymbol, getScanUrl } from 'utils/helpers';
import { Address } from 'viem';

import { UpdateButton } from '../';

import { DEX_NAME } from './consts';
import * as S from './RoutingModal.styles';

type RouteItem = {
  from: Address;
  to: Address;
  percent: number;
  dexes: {
    dex: string;
    percent: number;
  }[];
  nextStep: RouteItem[] | null;
};

type RouteStep = RouteItem[];

const getRouteStep = (from: Address, wowmaxQuote: WowmaxQuoteResponse): RouteStep => {
  const parts = wowmaxQuote.routes[0].parts;
  const allSteps = wowmaxQuote.routes;
  const step = allSteps.find((r) => r.from === from);
  if (!step) throw new Error(`Incorrect routing, error with ${from}`);

  const uniqTos = step.swaps.reduce(
    (arr: Address[], route) => (arr.includes(route.to) ? arr : [...arr, route.to]),
    []
  );
  const routeItems: RouteItem[] = uniqTos.map((to) => {
    const swaps = step.swaps.filter((s) => s.to === to);
    const part = swaps.reduce((sum, r) => sum + r.part, 0);
    const dexes: Record<string, number> = {};
    swaps.forEach((r) => {
      dexes[r.market.name] = (dexes[r.market.name] || 0) + r.part;
    });
    return {
      from,
      to,
      percent: (part / parts) * 100,
      dexes: Object.entries(dexes)
        .map(([dexName, dexPart]) => ({
          dex: dexName,
          percent: (dexPart / part) * 100,
        }))
        .sort((a, b) => b.percent - a.percent),
      nextStep: to === wowmaxQuote.to.address ? null : getRouteStep(to, wowmaxQuote),
    };
  });
  return routeItems.sort((a, b) => b.percent - a.percent);
};

export const RoutingModal = observer(() => {
  const { chainId } = store.web3Store;
  const { wowmaxResult, tokenIn, tokenOut, isRoutingOpen, closeRouting } = store.swapStore;

  useEffect(() => {
    if (wowmaxResult.isError) {
      closeRouting();
    }
  }, [wowmaxResult.isError, closeRouting]);

  const viewResult = useMemo(() => {
    const wowmax = wowmaxResult.data;
    if (!wowmax) return null;
    const result = getRouteStep(wowmax.from.address, wowmax);
    return result;
  }, [wowmaxResult.data]);

  const RouteRow = (routeItem: RouteItem) => {
    const address = routeItem.to;
    const token = TOKEN_BY_ADDRESS[chainId]?.[address];
    const routePercent = +routeItem.percent.toFixed(1);

    return (
      <S.RouteRow key={routeItem.from + routeItem.to}>
        <S.RouteArrow>
          {routePercent !== 100 && <S.RoutePercent>{routePercent}%</S.RoutePercent>}→
        </S.RouteArrow>
        <S.RouteItem>
          <S.RouteItemToken>
            <TokenLogo size={20} token={token} />
            <span>
              {token ? (
                cutTokenSymbol(token.symbol)
              ) : (
                <a
                  href={getScanUrl(`/token/${address}`, chainId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cutAddress(address)}
                </a>
              )}
            </span>
          </S.RouteItemToken>
          <S.Dexes>
            {routeItem.dexes.map((r, i) => (
              <S.Dex key={i}>
                {DEX_NAME[r.dex] || r.dex} {+r.percent.toFixed(1)}%
              </S.Dex>
            ))}
          </S.Dexes>
        </S.RouteItem>
        {routeItem.nextStep ? (
          <S.RouteColumn>{routeItem.nextStep.map(RouteRow)}</S.RouteColumn>
        ) : (
          <S.RouteArrow>→</S.RouteArrow>
        )}
      </S.RouteRow>
    );
  };

  return (
    <Modal isOpen={isRoutingOpen} onClose={closeRouting} maxWidth={700} title="Routing">
      <S.ModalContent>
        <S.HeaderRow>
          <S.Title>Routing</S.Title>
          <S.HeaderButtons>
            <UpdateButton />
          </S.HeaderButtons>
        </S.HeaderRow>
        {wowmaxResult.data && tokenIn && tokenOut && (
          <S.Amounts>
            <S.Amount>
              <TokenLogo token={tokenIn} size={20} />
              <span>
                {bn(wowmaxResult.data.amountIn).toView(tokenIn.decimals)}{' '}
                {cutTokenSymbol(tokenIn.symbol)}
              </span>
            </S.Amount>
            <S.AmountsArrow>→</S.AmountsArrow>
            <S.Amount>
              <TokenLogo token={tokenOut} size={20} />
              <span>
                {bn(wowmaxResult.data.amountOut).toView(tokenOut.decimals)}{' '}
                {cutTokenSymbol(tokenOut.symbol)}
              </span>
            </S.Amount>
          </S.Amounts>
        )}
        <S.DividerLine />
        <S.RoutingWrapper>
          {viewResult && (
            <S.Routing>
              <TokenLogo size={28} token={tokenIn} />
              <S.RoutingLine />
              <S.RouteColumn>{viewResult.map(RouteRow)}</S.RouteColumn>
              <S.RoutingLine />
              <TokenLogo size={28} token={tokenOut} />
            </S.Routing>
          )}
        </S.RoutingWrapper>
      </S.ModalContent>
    </Modal>
  );
});
